import { useI18n, type MessageKey } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { EmptyState, Field } from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import { usePlatformAnalyticsQuery } from '../api';
import { KpiCard } from '../components/KpiCard';

import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { MERCHANT_STATUS_LABEL_KEY } from '@/features/platform-admin/types';
import { consultationReason } from '@/lib/consultation-reason';
import { formatTnd } from '@/lib/format-money';

/**
 * `GET /api/v1/admin/analytics` — Analytics (S17) audit's own Platform
 * scope: GMV, order volume, merchant counts by status. Commissions and
 * growth are absent, not "Bientôt" — no commission model exists (OQ-01)
 * and this environment's real tenant history is too recent for a growth
 * trend to mean anything (the audit's own conclusion). Audited the
 * identical way `MerchantsPage` is: the console's default reason, or the
 * one typed in the field.
 */
export function PlatformAnalyticsPage(): ReactElement {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const analyticsQuery = usePlatformAnalyticsQuery(consultationReason(reason));

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink">{t('admin.analytics.title')}</h1>
        <p className="text-sm text-inkFaint">{t('admin.analytics.subtitle')}</p>
      </div>

      <div className="rounded-xl border border-line bg-surface p-4">
        <Field label={t('admin.common.reasonConsultationLabel')}>
          {(bind) => (
            <input
              {...bind}
              type="text"
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              maxLength={500}
              className="ecom-input"
              placeholder={t('admin.analytics.reasonPlaceholder')}
            />
          )}
        </Field>
      </div>

      {analyticsQuery.isPending ? (
        <TableSkeleton columns={4} />
      ) : analyticsQuery.isError ? (
        <EmptyState
          title={
            analyticsQuery.error.status === 403
              ? t('merchant.common.accessDenied')
              : t('admin.analytics.loadError')
          }
          description={
            analyticsQuery.error.status === 403
              ? t('admin.merchants.accessDeniedDescription')
              : t(resolveErrorMessageKey(analyticsQuery.error))
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
            <KpiCard label={t('admin.analytics.gmv')} value={formatTnd(analyticsQuery.data.gmv)} />
            <KpiCard
              label={t('admin.analytics.ordersAllShops')}
              value={String(analyticsQuery.data.orderCount)}
            />
            <KpiCard
              label={t('admin.analytics.activeMerchants')}
              value={String(analyticsQuery.data.activeMerchantCount)}
            />
            <KpiCard
              label={t('admin.analytics.pendingMerchants')}
              value={String(analyticsQuery.data.pendingMerchantCount)}
            />
          </div>

          <div>
            <h2 className="mb-3 font-display text-lg font-bold text-ink">
              {t('admin.analytics.byStatusTitle')}
            </h2>
            {analyticsQuery.data.merchantsByStatus.length === 0 ? (
              <EmptyState title={t('admin.analytics.noMerchants')} />
            ) : (
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
                {analyticsQuery.data.merchantsByStatus.map((entry) => {
                  const labelKey = (
                    MERCHANT_STATUS_LABEL_KEY as Record<string, MessageKey | undefined>
                  )[entry.status];
                  return (
                    <KpiCard
                      key={entry.status}
                      label={labelKey !== undefined ? t(labelKey) : entry.status}
                      value={String(entry.count)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
